import { matchPath } from "react-router";

import { environmentSelector } from "Reducers/environment";
import { routerLocationChange } from "Reducers/app/routerLocationChange";
import { selectCustomConfigDirName } from "Reducers/featureFlags/featureFlags.selectors";
import { startAppListening } from "Store/listenerMiddleware";

import loadConfigFile from "./thunk/loadConfigFile.thunk";

import type { RootState } from "Store/configureStore";
import type { AppStartListening } from "Store/listenerMiddleware";

/** Time budget for the environment (and its `head_commit`) to land in state. */
const ENVIRONMENT_READY_TIMEOUT_MS = 15_000;

/**
 * Task detail route pattern. The data router (`routes.console.ts`) only matches
 * down to `:organizationId/*`; everything below — project, environment, task —
 * is rendered via descendant `<Routes>`, so `routerLocationChange.params` never
 * carries the nested params. We re-derive them from the full `location.pathname`
 * with the same matcher the router uses.
 */
const TASK_ROUTE_PATTERN =
  "/:organizationId/:projectId/:environmentId/tasks/:taskName/*";

/**
 * Loads the env config file (`<configDir>/config.yaml`) when the user navigates
 * to a task detail route, so the task YAML tab renders without the component
 * dispatching on mount (no `useEffect` data fetching — see the frontend Redux
 * guidelines). Lives with the `git` slice it populates; the task route is only
 * the trigger.
 *
 * The config file needs the environment's `head_commit`, which may not be in
 * state yet at navigation time, so the listener waits for it via `condition()`
 * before dispatching. `loadConfigFile` itself dedupes already-loaded files.
 */
export const registerGitListeners = (startListening: AppStartListening) => {
  startListening({
    actionCreator: routerLocationChange,
    effect: async (
      action,
      { dispatch, getState, condition, cancelActiveListeners }
    ) => {
      const match = matchPath(
        TASK_ROUTE_PATTERN,
        action.payload.location.pathname
      );

      // Only task detail routes match; other navigations are ignored.
      if (!match) {
        return;
      }

      const { organizationId, projectId, environmentId, taskName } =
        match.params;
      if (!taskName || !organizationId || !projectId || !environmentId) {
        return;
      }

      // Supersede an in-flight wait from a previous navigation.
      cancelActiveListeners();

      const hasHeadCommit = (state: RootState) =>
        Boolean(
          environmentSelector(state, {
            organizationId,
            projectId,
            environmentId
          })?.head_commit
        );

      if (!hasHeadCommit(getState())) {
        const ready = await condition(
          (_, currentState) => hasHeadCommit(currentState),
          ENVIRONMENT_READY_TIMEOUT_MS
        );
        if (!ready) return;
      }

      const state = getState();
      const environment = environmentSelector(state, {
        organizationId,
        projectId,
        environmentId
      });

      dispatch(
        loadConfigFile({
          projectId,
          envHeadCommit: environment?.head_commit,
          configFolder: selectCustomConfigDirName(state)
        })
      );
    }
  });
};

// Register against the production middleware singleton.
registerGitListeners(startAppListening);
